import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Tracks the rendered width of a DOM element using ResizeObserver, so a
 * component can choose between variants based on its own width rather than
 * viewport width. Needed because Metric View cards get different column
 * allocations at different breakpoints (e.g. 7/12 at lg, 5/12 at xl), so
 * viewport-based Tailwind classes alone can't express "when the card itself
 * is narrow, switch to the list variant".
 *
 * Usage:
 *   const { ref, width } = useCardWidth<HTMLDivElement>();
 *   const variant = width < 500 ? 'list' : 'chart';
 *   return <div ref={ref}>...</div>;
 *
 * Returns width=0 until the first observation fires.
 */
export function useCardWidth<T extends HTMLElement>(): {
  ref: RefObject<T>;
  width: number;
} {
  // useRef<T>(null) yields RefObject<T | null> in newer @types/react; cast to
  // RefObject<T> so consumers (forwardRef Card, etc.) accept it directly.
  const ref = useRef<T>(null) as RefObject<T>;
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setWidth(entry.contentRect.width);
    });

    observer.observe(el);
    setWidth(el.getBoundingClientRect().width);

    return () => observer.disconnect();
  }, []);

  return { ref, width };
}
